import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./AdminActivity.css";

const AdminActivity = () => {
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredActivities, setFilteredActivities] = useState([]);

  // Fetch admin activity data from API
  const fetchAdminActivity = async () => {
    try {
      setLoading(true);
      const response = await axios.post('https://server.tradefxonline.com/admin_log_activity.php', {
        role: "admin"
      });
      
      if (response.data.success) {
        setActivities(response.data.data);
        setFilteredActivities(response.data.data);
        setTotalCount(response.data.count);
      } else {
        toast.error('Failed to fetch admin activity data');
      }
    } catch (error) {
      console.error('Error fetching admin activity:', error);
      toast.error('Error fetching admin activity data');
    } finally {
      setLoading(false);
    }
  };

  // Filter activities based on search and date range
  const filterActivities = useCallback(() => {
    let filtered = [...activities];

    // Search filter
    if (search.trim()) {
      filtered = filtered.filter(activity => 
        (activity.email && activity.email.toLowerCase().includes(search.toLowerCase())) ||
        (activity.action && activity.action.toLowerCase().includes(search.toLowerCase())) ||
        (activity.ip && activity.ip.includes(search)) ||
        (activity.uid && activity.uid.toString().includes(search))
      );
    }

    // Date range filter
    if (startDate) {
      filtered = filtered.filter(activity => {
        if (!activity.created_at) return false;
        const activityDate = new Date(activity.created_at).toISOString().split('T')[0];
        return activityDate >= startDate;
      });
    }

    if (endDate) {
      filtered = filtered.filter(activity => {
        if (!activity.created_at) return false;
        const activityDate = new Date(activity.created_at).toISOString().split('T')[0];
        return activityDate <= endDate;
      });
    }

    setFilteredActivities(filtered);
  }, [activities, search, startDate, endDate]);

  // Apply filters whenever search or date values change
  useEffect(() => {
    filterActivities();
  }, [filterActivities]);

  // Load data on component mount
  useEffect(() => {
    fetchAdminActivity();
  }, []);

  const handleReset = () => {
    setSearch("");
    setStartDate("");
    setEndDate("");
  };

  return (
    <div className="admin-activity-container">
      <h2 className="page-title">Staff Activity</h2>

      <div className="card">
        {/* Filters */}
        <div className="filter-row">
          <div className="form-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="form-group reset-btn">
            <button className="btn-reset" onClick={handleReset}>
              Reset
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="activity-table">
            <thead>
              <tr>
                <th>#</th>
                <th>User ID</th>
                <th>Email</th>
                <th>Role</th>
                <th>Action</th>
                <th>Success</th>
                <th>IP Address</th>
                <th>User Agent</th>
                <th>Date & Time</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: "center", padding: "20px" }}>
                    <div className="loading-spinner">Loading...</div>
                  </td>
                </tr>
              ) : filteredActivities.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: "center" }}>
                    No admin activity data available
                  </td>
                </tr>
              ) : (
                filteredActivities.map((activity, index) => (
                  <tr key={activity.id || index}>
                    <td>{index + 1}</td>
                    <td>{activity.uid || 'N/A'}</td>
                    <td>{activity.email || 'N/A'}</td>
                    <td>
                      <span className={`role-badge ${activity.role || 'unknown'}`}>
                        {activity.role || 'Unknown'}
                      </span>
                    </td>
                    <td>{activity.action || 'N/A'}</td>
                    <td>
                      <span className={`status-badge ${activity.success ? 'success' : 'failed'}`}>
                        {activity.success ? 'Success' : 'Failed'}
                      </span>
                    </td>
                    <td>{activity.ip || 'N/A'}</td>
                    <td className="user-agent-cell" title={activity.user_agent || 'N/A'}>
                      {activity.user_agent ? (
                        activity.user_agent.length > 50 
                          ? activity.user_agent.substring(0, 50) + '...' 
                          : activity.user_agent
                      ) : 'N/A'}
                    </td>
                    <td>{activity.created_at ? new Date(activity.created_at).toLocaleString() : 'N/A'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Results Count */}
        <div className="results-info">
          <p>Showing {filteredActivities.length} of {totalCount} entries {search || startDate || endDate ? '(filtered)' : ''}</p>
        </div>
      </div>

      {/* Footer */}
      <footer className="page-footer">
        <p>
          Copyright © 2025 <span>Ample Markets</span>. All rights reserved.
        </p>
        <p className="version">Version 2.0</p>
      </footer>
    </div>
  );
};

export default AdminActivity;
